import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import dayjs from 'dayjs';
import { LogIn, LogOut, Calendar as CalendarIcon, Utensils } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { Card } from '@/components/Card';
import { StatusBadge } from '@/components/Badge';
import Loader from '@/components/Loader';
import LiveWorkingHours from '@/components/LiveWorkingHours';
import ConfirmDialog from '@/components/ConfirmDialog';
import { attendanceService } from '@/services';
import { fmtDate, fmtTime, minutesToHours, isLunchOver } from '@/utils/format';

export default function Attendance() {
  const qc = useQueryClient();
  const [year, setYear] = useState(dayjs().year());
  const [month, setMonth] = useState(dayjs().month() + 1);
  const [confirmState, setConfirmState] = useState(null);

  const today = useQuery({ queryKey: ['attendance', 'today'], queryFn: attendanceService.today });
  const summary = useQuery({
    queryKey: ['attendance', 'summary', year, month],
    queryFn: () => attendanceService.mySummary({ year, month }),
  });

  const checkIn = useMutation({
    mutationFn: () => attendanceService.checkIn(),
    onSuccess: () => {
      toast.success('Checked in');
      qc.invalidateQueries({ queryKey: ['attendance'] });
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed'),
  });
  const checkOut = useMutation({
    mutationFn: () => attendanceService.checkOut(),
    onSuccess: () => {
      toast.success('Checked out');
      qc.invalidateQueries({ queryKey: ['attendance'] });
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed'),
  });
  const startLunch = useMutation({
    mutationFn: () => attendanceService.startLunch(),
    onSuccess: () => {
      toast.success('Lunch break started');
      qc.invalidateQueries({ queryKey: ['attendance'] });
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed'),
  });
  const endLunch = useMutation({
    mutationFn: () => attendanceService.endLunch(),
    onSuccess: () => {
      toast.success('Lunch break ended');
      qc.invalidateQueries({ queryKey: ['attendance'] });
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed'),
  });

  const att = today.data?.attendance;
  const onLunch = !!att?.lunchStartAt && !att?.lunchEndAt;
  const lunchDone = !!att?.lunchStartAt && !!att?.lunchEndAt;
  // Open session? Employees may check in/out several times a day.
  const isCheckedIn = !!att?.checkInAt && !att?.checkOutAt;
  const sessions = att?.sessions?.length ? att.sessions : (att?.checkInAt ? [{ checkInAt: att.checkInAt, checkOutAt: att.checkOutAt }] : []);
  const items = summary.data?.items || [];
  const sum = summary.data?.summary;

  // Build calendar grid
  const start = dayjs(`${year}-${String(month).padStart(2, '0')}-01`);
  const daysInMonth = start.daysInMonth();
  const firstDow = start.day();
  const itemMap = Object.fromEntries(items.map((it) => [it.date, it]));

  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    const date = start.date(d).format('YYYY-MM-DD');
    cells.push({ date, day: d, record: itemMap[date] });
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Attendance"
        subtitle="Check-in, check-out and your monthly attendance"
        actions={
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => checkIn.mutate()}
              disabled={isCheckedIn}
              className="btn-primary"
            >
              <LogIn size={16} /> {sessions.length > 0 && !isCheckedIn ? 'Check in again' : 'Check in'}
            </button>
            {onLunch ? (
              <button onClick={() => endLunch.mutate()} className="btn-secondary">
                <Utensils size={16} /> End lunch
              </button>
            ) : (
              <button
                onClick={() =>
                  setConfirmState({
                    title: 'Start lunch break?',
                    message: 'Your lunch timer starts now. You get one lunch break per day, so start it only when you actually break.',
                    confirmLabel: 'Start lunch',
                    icon: Utensils,
                    onConfirm: () => startLunch.mutateAsync().catch(() => {}),
                  })
                }
                disabled={!att?.checkInAt || !!att?.checkOutAt || lunchDone}
                className="btn-secondary"
                title={lunchDone ? 'Lunch already taken today' : 'Start your lunch break'}
              >
                <Utensils size={16} /> {lunchDone ? 'Lunch taken' : 'Start lunch'}
              </button>
            )}
            <button
              onClick={() =>
                setConfirmState({
                  title: 'Check out now?',
                  message: 'This ends the current session and pauses the live counter. You can check in again later — your time keeps adding up.',
                  confirmLabel: 'Check out',
                  icon: LogOut,
                  onConfirm: () => checkOut.mutateAsync().catch(() => {}),
                })
              }
              disabled={!att?.checkInAt || !!att?.checkOutAt}
              className="btn-secondary"
            >
              <LogOut size={16} /> Check out
            </button>
          </div>
        }
      />

      <div className="grid lg:grid-cols-4 gap-4">
        <Card>
          <div className="text-sm text-slate-500">Today</div>
          <div className="mt-1 text-xl font-semibold">{fmtDate(new Date())}</div>
          <div className="mt-3 space-y-1 text-sm">
            <div>In: <span className="font-medium">{fmtTime(att?.checkInAt)}</span></div>
            <div>Out: <span className="font-medium">{fmtTime(att?.checkOutAt)}</span></div>
            <div className="flex items-center gap-1">
              <Utensils size={13} className="text-slate-400" /> Lunch:{' '}
              <span className="font-medium">
                {att?.lunchStartAt
                  ? `${fmtTime(att.lunchStartAt)}${att.lunchEndAt ? ` – ${fmtTime(att.lunchEndAt)}` : ' (ongoing)'}`
                  : '—'}
              </span>
              {att?.lunchEndAt && <span className="text-xs text-slate-500">· {att.lunchMinutes || 0}m</span>}
              {isLunchOver(att?.lunchMinutes) && (
                <span className="text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300 font-semibold">Over 1h</span>
              )}
            </div>
            <div>
              Hours:{' '}
              <span className="font-medium">
                <LiveWorkingHours att={att} />
              </span>
              {att?.checkInAt && !att?.checkOutAt && (
                <span className="ml-1 text-[10px] uppercase text-emerald-500">live</span>
              )}
            </div>
          </div>

          {sessions.length > 0 && (
            <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-800">
              <div className="text-xs font-semibold text-slate-500 mb-1.5">Sessions today</div>
              <ul className="space-y-1">
                {sessions.map((s, i) => (
                  <li key={i} className="flex items-center justify-between text-xs">
                    <span className="flex items-center gap-1.5">
                      <span className="w-4 text-slate-400">{i + 1}.</span>
                      <span className="font-medium">{fmtTime(s.checkInAt)}</span>
                      <span className="text-slate-400">→</span>
                      <span className="font-medium">
                        {s.checkOutAt ? fmtTime(s.checkOutAt) : <span className="text-emerald-500">ongoing</span>}
                      </span>
                    </span>
                    <span className="text-slate-500">
                      {s.checkOutAt
                        ? minutesToHours(Math.max(0, Math.round((new Date(s.checkOutAt) - new Date(s.checkInAt)) / 60000)))
                        : '—'}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {att?.checkInMeta && (
            <div className="mt-3 text-xs text-slate-500">
              <div>IP: {att.checkInMeta.ip}</div>
              <div>{att.checkInMeta.browser} · {att.checkInMeta.os}</div>
            </div>
          )}
        </Card>
        <Card>
          <div className="text-sm text-slate-500">This month</div>
          <div className="text-3xl font-bold mt-1">{sum?.totalHours || 0}h</div>
          <div className="text-xs text-slate-500">Total working hours</div>
        </Card>
        <Card>
          <div className="text-sm text-slate-500">Present days</div>
          <div className="text-3xl font-bold mt-1">{sum?.present || 0}</div>
          <div className="text-xs text-slate-500">{sum?.late || 0} late · {sum?.halfDay || 0} half-day</div>
        </Card>
        <Card>
          <div className="text-sm text-slate-500">Leaves / WFH</div>
          <div className="text-3xl font-bold mt-1">{(sum?.leave || 0) + (sum?.wfh || 0)}</div>
          <div className="text-xs text-slate-500">{sum?.leave || 0} leave · {sum?.wfh || 0} WFH</div>
        </Card>
      </div>

      <Card>
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="font-semibold flex items-center gap-2"><CalendarIcon size={18} /> Calendar</div>
          <div className="flex items-center gap-2">
            <select className="input w-32" value={month} onChange={(e) => setMonth(+e.target.value)}>
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                <option key={m} value={m}>{dayjs().month(m - 1).format('MMMM')}</option>
              ))}
            </select>
            <select className="input w-28" value={year} onChange={(e) => setYear(+e.target.value)}>
              {Array.from({ length: 5 }, (_, i) => dayjs().year() - 2 + i).map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        </div>

        {summary.isLoading ? (
          <Loader />
        ) : (
          <div>
            <div className="grid grid-cols-7 text-center text-xs font-semibold text-slate-500 mb-2">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
                <div key={d}>{d}</div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1 sm:gap-2">
              {cells.map((c, idx) =>
                c ? (
                  <div
                    key={idx}
                    className={`aspect-square rounded-lg border text-xs p-1 sm:p-2 flex flex-col items-start justify-between ${
                      c.record
                        ? c.record.status === 'absent'
                          ? 'bg-rose-50 dark:bg-rose-500/10 border-rose-200 dark:border-rose-500/30'
                          : c.record.isLate
                          ? 'bg-amber-50 dark:bg-amber-500/10 border-amber-200 dark:border-amber-500/30'
                          : 'bg-emerald-50 dark:bg-emerald-500/10 border-emerald-200 dark:border-emerald-500/30'
                        : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800'
                    }`}
                  >
                    <div className="font-semibold">{c.day}</div>
                    {c.record && (
                      <div className="text-[10px] text-slate-500 truncate">
                        {minutesToHours(c.record.workingMinutes)}
                      </div>
                    )}
                  </div>
                ) : (
                  <div key={idx} />
                )
              )}
            </div>
          </div>
        )}
      </Card>

      <Card>
        <div className="font-semibold mb-3">History (this month)</div>
        <div className="table-wrap">
          <table className="min-w-full">
            <thead>
              <tr>
                <th className="th">Date</th>
                <th className="th">Check In</th>
                <th className="th">Check Out</th>
                <th className="th">Lunch</th>
                <th className="th">Hours</th>
                <th className="th">Status</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td className="td text-center text-slate-500" colSpan={6}>No records</td></tr>
              )}
              {items.map((r) => (
                <tr key={r._id}>
                  <td className="td font-medium">{fmtDate(r.date)}</td>
                  <td className="td">{fmtTime(r.checkInAt)}</td>
                  <td className="td">{fmtTime(r.checkOutAt)}</td>
                  <td className="td whitespace-nowrap">
                    {r.lunchStartAt ? (
                      <span className={isLunchOver(r.lunchMinutes) ? 'text-amber-600 dark:text-amber-400 font-medium' : ''}>
                        {fmtTime(r.lunchStartAt)}{r.lunchEndAt ? ` – ${fmtTime(r.lunchEndAt)}` : ''}{r.lunchMinutes ? ` · ${r.lunchMinutes}m` : ''}
                        {isLunchOver(r.lunchMinutes) && ' ⚠'}
                      </span>
                    ) : '—'}
                  </td>
                  <td className="td">{minutesToHours(r.workingMinutes)}</td>
                  <td className="td"><StatusBadge status={r.isLate ? 'late' : r.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <ConfirmDialog state={confirmState} onClose={() => setConfirmState(null)} />
    </div>
  );
}
